import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Footer from "../components/Footer";
import Header from "../components/Header";
import { Loader } from "../components/Loader";
import { LoadingButton } from "../components/LoadingButton";
import API from "../helper/AxiosInterceptor";

const OTPVerification = () => {
  const [timer, setTimer] = useState(60);
  const [isDisabled, setIsDisabled] = useState(true); // For Resend Button
  const [isLoading, setIsLoading] = useState(false);

  // Separate states for Email OTP and Google Auth OTP
  const [emailOtp, setEmailOtp] = useState(["", "", "", "", "", ""]);
  const [googleOtp, setGoogleOtp] = useState(["", "", "", "", "", ""]);

  const [errors, setErrors] = useState({ otp: "" });

  const location = useLocation();
  const [loginData, setLoginData] = useState(location.state?.data || {});

  // State to track progress
  const [isEmailVerified, setIsEmailVerified] = useState(false);

  // Refs for focusing
  const emailOtpRefs = useRef([]);
  const googleOtpRefs = useRef([]);

  const navigate = useNavigate();

  useEffect(() => {
    if (!loginData?.email) {
      navigate("/login");
    }
  }, [loginData?.email]);

  const handleChange = (e, index, type) => {
    const { value } = e.target;
    if (/^[0-9]$/.test(value) || value === "") {
      if (type === 'email') {
        const newOtp = [...emailOtp];
        newOtp[index] = value;
        setEmailOtp(newOtp);
        if (value !== "" && index < 5) emailOtpRefs.current[index + 1].focus();
      } else {
        const newOtp = [...googleOtp];
        newOtp[index] = value;
        setGoogleOtp(newOtp);
        if (value !== "" && index < 5) googleOtpRefs.current[index + 1].focus();
      }
    }
  };

  const handleKeyDown = (e, index, type) => {
    if (e.key === "Backspace" && type === 'email' && emailOtp[index] === "" && index > 0) {
      emailOtpRefs.current[index - 1].focus();
    } else if (e.key === "Backspace" && type === 'google' && googleOtp[index] === "" && index > 0) {
      googleOtpRefs.current[index - 1].focus();
    }
    // Add arrow key navigation if needed
  };

  // ------------------------------------
  // VERIFY EMAIL OTP
  // ------------------------------------
  const handleVerifyEmail = async (e) => {
    e.preventDefault();
    if (emailOtp.some((digit) => digit === "")) {
      setErrors({ otp: "Please enter a valid 6-digit Email OTP" });
      return;
    }

    try {
      setIsLoading(true);
      const response = await API.post(
        `${process.env.REACT_APP_API_URL}/auth/merchant-otp-verify`,
        {
          otp: Number(emailOtp.join("")),
          email: loginData?.email,
        }
      );

      if (response?.data.success) {
        // If Step 2 (Google Auth) is required
        if (response.data.step2_required) {
          toast.success("Email Verified! Please enter Google Authenticator Code.");
          setIsEmailVerified(true);
          setErrors({ otp: "" });
          // Update Login Data to reflect we are now on google auth step
          setLoginData(prev => ({ ...prev, '2fa_type': 'google_auth', 'requires_2fa': true }));
          // Focus Google Input (delayed to allow render)
          setTimeout(() => {
            if (googleOtpRefs.current[0]) googleOtpRefs.current[0].focus();
          }, 100);
        } else {
          // Login Complete
          finishLogin(response.data.access_token);
        }
      }
    } catch (error) {
      handleError(error);
    } finally {
      setIsLoading(false);
    }
  };

  // ------------------------------------
  // VERIFY GOOGLE OTP
  // ------------------------------------
  const handleVerifyGoogle = async (e) => {
    e.preventDefault();
    if (googleOtp.some((digit) => digit === "")) {
      setErrors({ otp: "Please enter a valid 6-digit Google Auth Code" });
      return;
    }

    try {
      setIsLoading(true);
      const response = await API.post(
        `${process.env.REACT_APP_API_URL}/auth/merchant-otp-verify`,
        {
          otp: Number(googleOtp.join("")),
          email: loginData?.email,
        }
      );

      if (response?.data.success) {
        finishLogin(response.data.access_token);
      }
    } catch (error) {
      handleError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const finishLogin = (token) => {
    localStorage.setItem("token", token);
    toast.success("Login successfully!");
    navigate("/dashboard");
  }

  const handleError = (error) => {
    const msg = error.response?.data?.message || "Verification Failed";
    toast.error(Array.isArray(msg) ? msg[0] : msg);
    setErrors({ otp: typeof msg === 'string' ? msg : "Error occurred" });
  }

  const resendOTP = async () => {
    try {
      setIsLoading(true);
      const response = await API.post(`${process.env.REACT_APP_API_URL}/auth/merchant-login`, loginData);
      if (response?.data.success) {
        toast.success("OTP resent!");
        setIsDisabled(true);
        setTimer(60);
      }
    } catch (error) {
      toast.error("Failed to resend OTP");
    } finally {
      setIsLoading(false);
    }
  };

  // Effect hook to handle countdown
  useEffect(() => {
    let interval = null;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prevTimer) => prevTimer - 1);
      }, 1000);
    } else if (timer === 0) {
      setIsDisabled(false);
    }
    return () => clearInterval(interval);
  }, [timer]);

  // if (isLoading) return <Loader />;

  // DETERMINE VISIBILITY
  // Default to Email section if 2fa_type is missing but we have data (fallback)
  const showEmailSection = (!loginData['2fa_type'] || loginData['2fa_type'] === 'email') || isEmailVerified;
  const showGoogleSection = loginData['2fa_type'] === 'google_auth' || loginData['next_2fa_type'] === 'google_auth';

  const isGoogleLocked = (!loginData['2fa_type'] || loginData['2fa_type'] === 'email') && !isEmailVerified;

  return (
    <>
      <Header />
      <div className="site-bg">
        <div className="inner-page-warper section-padding">
          <div className="container">
            <div className="form-box" style={{ maxWidth: "600px" }}>

              <div className="form-box-inner white-bg p-4">
                <h2 className="mb-4 text-center" style={{ fontSize: "24px", fontWeight: "700", color: "#333" }}>Two-Factor Verification</h2>

                {/* --- ERROR DISPLAY --- */}
                <div style={{ minHeight: "20px", marginBottom: "10px", textAlign: "center" }}>
                  {errors.otp && <span className="error" style={{ color: "red", fontWeight: "bold" }}>{errors.otp}</span>}
                </div>

                {/* --- EMAIL OTP SECTION --- */}
                {showEmailSection && (
                  <div className={`otp-section mb-4 pb-4 ${isEmailVerified ? "opacity-75" : ""}`} style={{ borderBottom: showGoogleSection ? "1px solid #eee" : "none" }}>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h5 className="m-0"><i className="ti ti-mail me-2"></i>Email Verification</h5>
                      {isEmailVerified && <span className="badge bg-success">Verified <i className="ti ti-check"></i></span>}
                    </div>

                    <div className="form-group-inner otp-input justify-content-center d-flex" style={{ flexWrap: "nowrap", gap: "8px" }}>
                      {emailOtp.map((digit, index) => (
                        <input
                          key={`email-${index}`}
                          ref={(el) => (emailOtpRefs.current[index] = el)}
                          type="text"
                          className="form-control text-center p-0"
                          style={{ width: "42px", height: "42px", fontSize: "18px", backgroundColor: isEmailVerified ? "#f0fff4" : "#fff" }}
                          value={digit}
                          disabled={isEmailVerified}
                          onChange={(e) => handleChange(e, index, 'email')}
                          onKeyDown={(e) => handleKeyDown(e, index, 'email')}
                          maxLength="1"
                        />
                      ))}
                    </div>

                    {!isEmailVerified && (
                      <div className="mt-3 text-center">
                        <div className="forgot-btn mb-3">
                          <a style={{ cursor: isDisabled ? "not-allowed" : "pointer", color: isDisabled ? "#ccc" : "#0d6efd", textDecoration: "underline" }}
                            onClick={!isDisabled ? resendOTP : null}>
                            {isDisabled ? `Resend OTP in ${timer}s` : "Resend OTP"}
                          </a>
                        </div>
                        <LoadingButton
                          className="btn btn-primary w-100"
                          onClick={handleVerifyEmail}
                          isLoading={isLoading}
                        >
                          Verify Email
                        </LoadingButton>
                      </div>
                    )}
                  </div>
                )}

                {/* --- GOOGLE AUTH SECTION --- */}
                {showGoogleSection && (
                  <div className={`otp-section ${isGoogleLocked ? "opacity-50" : ""}`} style={{ pointerEvents: isGoogleLocked ? "none" : "auto" }}>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h5 className="m-0"><i className="ti ti-brand-google me-2"></i>Google Authenticator</h5>
                      {isGoogleLocked && <span className="badge bg-secondary">Locked</span>}
                    </div>

                    <div className="form-group-inner otp-input justify-content-center d-flex" style={{ flexWrap: "nowrap", gap: "8px" }}>
                      {googleOtp.map((digit, index) => (
                        <input
                          key={`google-${index}`}
                          ref={(el) => (googleOtpRefs.current[index] = el)}
                          type="text"
                          className="form-control text-center p-0"
                          style={{ width: "42px", height: "42px", fontSize: "18px" }}
                          value={digit}
                          disabled={isGoogleLocked}
                          onChange={(e) => handleChange(e, index, 'google')}
                          onKeyDown={(e) => handleKeyDown(e, index, 'google')}
                          maxLength="1"
                        />
                      ))}
                    </div>

                    {!isGoogleLocked && (
                      <div className="mt-3 text-center">
                        <LoadingButton
                          className="btn btn-success w-100"
                          onClick={handleVerifyGoogle}
                          isLoading={isLoading}
                        >
                          Verify & Login
                        </LoadingButton>
                      </div>
                    )}
                  </div>
                )}

              </div>

              {/* Decorative Shapes */}
              <div className="form-shape-box1"><img src="images/shape1.png" alt="shape" /></div>
              <div className="form-shape-box2"><img src="images/shape2.png" alt="shape" /></div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default OTPVerification;
